import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import { NO_DATA_AVAILABLE } from '../../../constants/common'
import {Flex, Form, Modal, Row, Space} from 'antd'
import {DownloadOutlined, ExclamationCircleOutlined} from '@ant-design/icons'
import {useIntl} from 'react-intl'
import {deleteRequest, getRequest} from '../../../services/base'
import {NavContext} from '../../../context/global_context'
import useDebouncedValue from '../../../utils/common_function'
import {buildPageUrl, downloadFileBlob, getDeleteTargetPage} from '../../../utils/common'
import InputText from '../../common/InputText'
import Button from '../../common/Button'
import {SELECT_TEST_ZONE, SET_TEST_ZONE_LIST, TestZoneContext,} from './context/context'
import TestZoneTable from './TestZoneTable'
import TestZoneFormModal from './form'
import { notifyError } from '../../../utils/notifications'

const TestZones = () => {
  const intl = useIntl()
  const [state, dispatch] = useContext(TestZoneContext)
  const [, navDispatch] = useContext(NavContext)

  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({})
  const currentUrl = useRef('/api/test_zone/')

  const [modal, contextHolder] = Modal.useModal()
  const debouncedSearch = useDebouncedValue(search, 500)

  const getTestZoneListData = async (url) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'testZones.fetchingData' }),
        showLoader: true,
      },
      notification: {},
    })

    currentUrl.current = url ? url : currentUrl.current
    let res = await getRequest(currentUrl.current)
    setPagination(res.pagination)
    dispatch({ type: SET_TEST_ZONE_LIST, data: res.results })

    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })

    // After the loader dispatch above: a 'loader' action overwrites `notification`,
    // so notifying before it would be wiped out in the same React batch.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'testZones.loadError' }))
    }
  }

  useEffect(() => {
    getTestZoneListData()
  }, [])

  useEffect(() => {
    const url = `/api/test_zone/?search=${search}`
    currentUrl.current = url
    getTestZoneListData()
  }, [debouncedSearch])

  const deleteTestZone = async (record) => {
    let res = await deleteRequest(`/api/test_zone/${record.id}/`)
    if (res?.status) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'testZones.deleteSuccess' }),
        },
      })
      currentUrl.current = buildPageUrl(currentUrl.current, getDeleteTargetPage(pagination))
      getTestZoneListData()
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'testZones.deleteError' }),
        },
      })
    }
  }

  const confirmDelete = (record) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'testZones.confirmDelete' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage(
        { id: 'testZones.confirmDeleteContent' },
        { specifier: record.specifier }
      ),
      onOk: () => deleteTestZone(record),
      centered: true,
      maskClosable: true,
    })
  }

  const downloadTestZone = async () => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'testZones.downloadCaption' }),
        showLoader: true,
      },
      notification: {},
    })

    let res = await getRequest(`/api/test_zone/download/`, true, true)
    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })
    // `res?.msg` was redundant here: base.js sets status false on every failure,
    // and an error body is never a Blob.
    if (res?.status === false || !(res instanceof Blob)) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type:
            res?.code === NO_DATA_AVAILABLE ? 'info' : 'error',
          msg: intl.formatMessage({
            id:
              res?.code === NO_DATA_AVAILABLE
                ? 'generic.noDataToExport'
                : 'testZones.downloadError',
          }),
        },
      })
    } else {
      downloadFileBlob(res, 'test_zones.csv')
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'testZones.downloadCompleted' }),
        },
      })
    }
  }

  const onCreateTestZone = () => {
    dispatch({
      type: SELECT_TEST_ZONE,
      data: { selected_test_zone: {}, show_test_zone_popup: true },
    })
  }

  const onEditTestZone = (record) => {
    dispatch({
      type: SELECT_TEST_ZONE,
      data: { selected_test_zone: record, show_test_zone_popup: true },
    })
  }

  const dataSource = useMemo(() => {
    if (!state?.test_zone_list?.length) return []
    return state.test_zone_list.map((item, index) => ({
      ...item,
      key: `test-zone-${index}`,
      rowKey: `test-zone-${item.id}`,
      index,
    }))
  }, [state.test_zone_list])

  return (
    <>
      {contextHolder}

      {state.show_test_zone_popup && (
        <TestZoneFormModal getTestZoneListData={getTestZoneListData} />
      )}

      <div className="page sub-page">
        <Row className="body">
          <Form className="full-width administration-form">
            <Flex justify="space-between" gap={16} className="full-width">
              <InputText
                name="search_test_zones"
                type="search"
                placeholder={intl.formatMessage({
                  id: 'testZones.searchPlaceholder',
                })}
                value={search}
                isRequired={false}
                className="full-width"
                wrapperClass="search-test-zones"
                onChange={(value) => setSearch(value)}
              />
              <Space size="middle" align="center">
                <Button onClick={downloadTestZone} icon={<DownloadOutlined />}>
                  {intl.formatMessage({ id: 'testZones.export' })}
                </Button>
                <Button onClick={onCreateTestZone}>
                  {intl.formatMessage({ id: 'testZones.createNew' })}
                </Button>
              </Space>
            </Flex>
          </Form>

          <Flex style={{ width: '100%' }}>
            <TestZoneTable
              dataSource={dataSource}
              pagination={pagination}
              getTestZoneListData={getTestZoneListData}
              onEdit={onEditTestZone}
              onDelete={confirmDelete}
            />
          </Flex>
        </Row>
      </div>
    </>
  )
}

export default TestZones
